import { App as AntApp, Alert, Button, Descriptions, Form, Input, InputNumber, Modal, Space, Steps, Typography } from 'antd';
import { useEffect, useState } from 'react';

import type { WalletBalance } from '../../../src/types.ts';
import { api, errorText } from '../lib/api.ts';
import { fmtPrice } from '../lib/format.ts';

interface Props {
  open: boolean;
  wallet: WalletBalance | null;
  onClose: () => void;
  onChanged: () => void;
}

interface Fee {
  amount: number;
  currency: string;
  description?: string;
  descriptionEn?: string;
}

/**
 * Move money to another etravel wallet: find the recipient by mobile number,
 * enter the amount, confirm the fees with the one-time code etravel sends.
 */
export function TransferModal({ open, wallet, onClose, onChanged }: Props) {
  const { message } = AntApp.useApp();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [recipient, setRecipient] = useState<{ id: number; name?: string } | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [fees, setFees] = useState<Fee[]>([]);
  const [otp, setOtp] = useState('');
  const [done, setDone] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setRecipient(null);
    setAmount(null);
    setFees([]);
    setOtp('');
    setDone(false);
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const lookup = async (v: { mobile: string }) => {
    setBusy(true);
    try {
      const c = await api.customerByMobile(v.mobile.trim());
      if (!c?.id) throw new Error('No etravel account uses that number.');
      setRecipient({ id: c.id, name: typeof c.name === 'string' ? c.name : undefined });
      setStep(1);
    } catch (err) {
      message.error(errorText(err), 6);
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async () => {
    if (!recipient || !amount) return;
    setBusy(true);
    try {
      const [, f] = await Promise.all([api.sendOtp().catch(() => null), api.transferFees(recipient.id, amount)]);
      setFees(Array.isArray(f) ? f : []);
      setCooldown(45);
      setStep(2);
    } catch (err) {
      message.error(errorText(err), 6);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.sendOtp();
      setCooldown(45);
      message.success('Code sent again.');
    } catch (err) {
      message.error(errorText(err));
    }
  };

  const confirm = async () => {
    if (!recipient || !amount || otp.length < 4) return;
    setBusy(true);
    try {
      await api.transfer(recipient.id, amount, otp.trim());
      setDone(true);
      onChanged();
    } catch (err) {
      message.error(errorText(err), 8);
    } finally {
      setBusy(false);
    }
  };

  const feeTotal = fees.reduce((s, f) => s + Number(f.amount || 0), 0);
  const balance = wallet?.balance ?? null;
  const currency = wallet?.currency;
  const overdrawn = balance != null && amount != null && amount + feeTotal > balance;

  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Transfer to another wallet" destroyOnHidden width={560}>
      {done ? (
        <Space orientation="vertical" size={12} style={{ width: '100%', textAlign: 'center' }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Transfer sent
          </Typography.Title>
          <Typography.Text>
            {fmtPrice(amount, currency)} to {recipient?.name ?? 'the recipient'}.
          </Typography.Text>
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </Space>
      ) : (
        <>
          <Steps size="small" current={step} style={{ marginBottom: 20 }} items={[{ title: 'Recipient' }, { title: 'Amount' }, { title: 'Confirm' }]} />
          {balance != null && (
            <Typography.Paragraph type="secondary">
              Current balance <strong>{fmtPrice(balance, currency)}</strong>
            </Typography.Paragraph>
          )}

          {step === 0 && (
            <Form layout="vertical" requiredMark={false} onFinish={(v) => void lookup(v as { mobile: string })}>
              <Form.Item name="mobile" label="Recipient's mobile number" rules={[{ required: true, message: 'Enter the number registered on etravel' }]} extra="The number the recipient signed up to etravel with, e.g. 0912345678 or 218912345678.">
                <Input inputMode="tel" placeholder="09XXXXXXXX" autoFocus />
              </Form.Item>
              <Button type="primary" htmlType="submit" block loading={busy}>
                Find account
              </Button>
            </Form>
          )}

          {step === 1 && recipient && (
            <Space orientation="vertical" size={12} style={{ width: '100%' }}>
              <Alert type="info" showIcon message="Check the recipient's name before sending." description={<strong>{recipient.name ?? `Account #${recipient.id}`}</strong>} />
              <div>
                <Typography.Text strong>Amount{currency ? ` (${currency})` : ''}</Typography.Text>
                <InputNumber min={1} precision={2} style={{ width: '100%', marginTop: 6 }} value={amount} onChange={(v) => setAmount(v == null ? null : Number(v))} autoFocus />
              </div>
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <Button onClick={() => setStep(0)}>Back</Button>
                <Button type="primary" loading={busy} disabled={!amount} onClick={() => void sendCode()}>
                  Send confirmation code
                </Button>
              </Space>
            </Space>
          )}

          {step === 2 && recipient && amount != null && (
            <Space orientation="vertical" size={12} style={{ width: '100%' }}>
              <Descriptions
                size="small"
                bordered
                column={1}
                items={[
                  { key: 'to', label: 'To', children: recipient.name ?? `Account #${recipient.id}` },
                  { key: 'amt', label: 'Amount', children: fmtPrice(amount, currency) },
                  ...fees.map((f, i) => ({ key: `f${i}`, label: f.descriptionEn || f.description || 'Fee', children: fmtPrice(f.amount, f.currency) })),
                  { key: 'tot', label: <strong>Leaves your wallet</strong>, children: <strong>{fmtPrice(amount + feeTotal, currency)}</strong> },
                ]}
              />
              {overdrawn && <Alert type="error" showIcon message="Not enough balance for this transfer." />}
              <div>
                <Typography.Text strong>Code sent to your mobile / email</Typography.Text>
                <Input.OTP length={4} value={otp} onChange={setOtp} style={{ marginTop: 6 }} />
              </div>
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <Space>
                  <Button onClick={() => setStep(1)}>Back</Button>
                  <Button type="link" disabled={cooldown > 0} onClick={() => void resend()}>
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </Button>
                </Space>
                <Button type="primary" loading={busy} disabled={otp.length < 4 || overdrawn} onClick={() => void confirm()}>
                  Confirm transfer
                </Button>
              </Space>
            </Space>
          )}
        </>
      )}
    </Modal>
  );
}
